// 글 속 주소(http·https·www.)를 누를 수 있는 링크로 바꾼다 — 누르면 새 탭에서 열린다.
//
// 채팅·공지처럼 글자를 그대로 보여 주는 곳에서는 주소를 붙여 넣어도 눌러지지 않아
// 일일이 복사해 주소창에 넣어야 했다. HTML 을 끼워 넣지 않고 React 노드로만 조립하므로
// 글에 태그를 써도 실행되지 않는다(XSS 안전).
import type { CSSProperties, ReactNode } from "react";

// 공백·꺾쇠·따옴표·닫는 괄호에서 주소가 끝난다. 한글 문장 뒤에 바로 붙은 주소도 잡는다.
const URL_RE = /(?:https?:\/\/|www\.)[^\s<>"'）)\]]+/gi;
// 문장 끝에 붙은 마침표·쉼표 등은 주소에 넣지 않는다.
const TRAIL_RE = /[.,;:!?]+$/;

export type LinkPart = { text: string; href?: string };

/** 글을 '그냥 글자' 와 '주소' 조각으로 나눈다. */
export function splitLinks(text: string): LinkPart[] {
  const out: LinkPart[] = [];
  let last = 0;
  URL_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = URL_RE.exec(text))) {
    const raw = m[0].replace(TRAIL_RE, "");
    if (!raw) continue;
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    const href = /^www\./i.test(raw) ? `https://${raw}` : raw;
    out.push({ text: raw, href });
    last = m.index + raw.length;
    URL_RE.lastIndex = last;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

export const LINK_STYLE: CSSProperties = {
  color: "var(--p-600)", textDecoration: "underline", wordBreak: "break-all", cursor: "pointer",
};

/**
 * 글을 그대로 보여 주되 주소만 새 탭 링크로. 주소가 아닌 조각은 renderText 로 넘겨
 * 다른 꾸밈(채팅의 @멘션 강조 등)을 이어서 할 수 있게 한다.
 */
export function LinkifiedText({ text, linkStyle, renderText }: {
  text: string;
  linkStyle?: CSSProperties;
  renderText?: (chunk: string, key: number) => ReactNode;
}) {
  return (
    <>
      {splitLinks(text).map((p, i) => p.href ? (
        <a key={i} href={p.href} target="_blank" rel="noopener noreferrer"
           // 말풍선·목록 행이 클릭을 가로채(메뉴 열기·선택 등) 링크가 안 열리는 일이 없게 한다.
           onClick={(e) => e.stopPropagation()}
           style={linkStyle ?? LINK_STYLE}>{p.text}</a>
      ) : (
        renderText ? <span key={i}>{renderText(p.text, i)}</span> : <span key={i}>{p.text}</span>
      ))}
    </>
  );
}
